import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlaskConicalIcon, CheckCircleIcon, BanIcon, UsersIcon } from 'lucide-react';
import { useKorintConfig } from 'korint-sdk/react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Title } from '@folio/components/ui/text';
import { Stack } from '@folio/components/ui/flex';

const ROADMAP = {
  capabilities: [
    'conversation.beta.capability_searchPolicies',
    'conversation.beta.capability_comparePolicies',
    'conversation.beta.capability_createMta',
  ],
  limitations: [
    'conversation.beta.limitation_viewQuotes',
    'conversation.beta.limitation_createQuote',
    'conversation.beta.limitation_unpaidManagement',
    'conversation.beta.limitation_signatureManagement',
    'conversation.beta.limitation_distributorBrokers',
    'conversation.beta.limitation_contractDocuments',
    'conversation.beta.limitation_portfolioStats',
    'conversation.beta.limitation_archiveQuotesPolicies',
  ],
  access: ['conversation.beta.access_info'],
} as const;

export function BannerConversation() {
  const { t } = useTranslation();
  const { clientOptions } = useKorintConfig();
  const [open, setOpen] = useState(false);

  // Only used by single-tenant apps, where tenant is always set.
  const tenant = (clientOptions.tenant ?? '').toLowerCase();
  const tenantName = tenant.charAt(0).toUpperCase() + tenant.slice(1);
  const bannerMessageKey =
    tenant === 'tutassur'
      ? 'conversation.beta.bannerMessagePaid'
      : 'conversation.beta.bannerMessage';

  return (
    <>
      <Alert variant="info" className="mx-4 mb-2 max-w-[calc(100%-2rem)]">
        <FlaskConicalIcon />
        <AlertDescription>
          {t(bannerMessageKey)}
          <Button
            variant="link"
            className="ml-1 h-auto p-0 text-inherit"
            onClick={() => setOpen(true)}
          >
            {t('conversation.beta.detailsLink')}
          </Button>
        </AlertDescription>
      </Alert>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <Stack>
            <section>
              <Title order={4} size="sm" className="mb-2 flex items-center gap-2 font-medium">
                <CheckCircleIcon className="size-4" />
                {t('conversation.beta.capabilitiesTitle')}
              </Title>
              <Stack asChild gap={1}>
                <ul className="list-disc text-sm text-muted-foreground pl-10">
                  {ROADMAP.capabilities.map(key => (
                    <li key={key}>{t(key)}</li>
                  ))}
                </ul>
              </Stack>
            </section>

            <section>
              <Title order={4} size="sm" className="mb-2 flex items-center gap-2 font-medium">
                <BanIcon className="size-4" />
                {t('conversation.beta.limitationsTitle')}
              </Title>
              <Stack asChild gap={1}>
                <ul className="list-disc text-sm text-muted-foreground pl-10">
                  {ROADMAP.limitations.map(key => (
                    <li key={key}>{t(key)}</li>
                  ))}
                </ul>
              </Stack>
            </section>

            <section>
              <Title order={4} size="sm" className="mb-2 flex items-center gap-2 font-medium">
                <UsersIcon className="size-4" />
                {t('conversation.beta.accessTitle')}
              </Title>
              <Stack asChild gap={1}>
                <ul className="list-disc text-sm text-muted-foreground pl-10">
                  {ROADMAP.access.map(key => (
                    <li key={key}>{t(key, { tenant: tenantName })}</li>
                  ))}
                </ul>
              </Stack>
            </section>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
